import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw, Activity, UserPlus, Mail, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/** Tiny dependency-free SVG sparkline. */
const Sparkline = ({ series, color = "#7737F6", testid }) => {
  const vals = series.map((p) => p.count);
  const max = Math.max(1, ...vals);
  const w = 220;
  const h = 44;
  const step = vals.length > 1 ? w / (vals.length - 1) : w;
  const pts = vals.map((v, i) => `${(i * step).toFixed(1)},${(h - (v / max) * (h - 6) - 3).toFixed(1)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-11" preserveAspectRatio="none" data-testid={testid}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
      {vals.map((v, i) => (
        <circle key={i} cx={(i * step).toFixed(1)} cy={(h - (v / max) * (h - 6) - 3).toFixed(1)} r={v === max && v > 0 ? 2.6 : 1.4} fill={color} />
      ))}
    </svg>
  );
};

const MetricCard = ({ icon: Icon, label, value, sub, series, color, testid }) => (
  <div className="bg-white border border-zinc-200 p-5" data-testid={testid}>
    <div className="flex items-center gap-2 text-zinc-500">
      <Icon size={14} className="text-[#7737F6]" />
      <span className="text-[11px] font-semibold tracking-[0.16em] uppercase">{label}</span>
    </div>
    <div className="mt-3 flex items-baseline gap-2">
      <span className="text-[32px] font-semibold text-zinc-900 tabular-nums leading-none">{value}</span>
      {sub ? <span className="text-[12px] text-zinc-500">{sub}</span> : null}
    </div>
    {series ? <div className="mt-3"><Sparkline series={series} color={color} testid={`${testid}-spark`} /></div> : null}
  </div>
);

const CrossAppPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/cross-app/activity");
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load cross-app activity");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-zinc-500 text-[13px]">Loading cross-app activity…</div>;
  if (!data) return null;

  const ac = data.assessment_calls;
  const lr = data.leads_from_erio;

  return (
    <div data-testid="cross-app-panel">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="ek-heading text-xl">Cross-app activity</h2>
          <p className="text-zinc-500 text-[13px] mt-1 max-w-2xl">
            How Ekselens and its sister product <strong>ERIO</strong> are exchanging value — assessment add-on demand, leads ERIO forwards into the shared hub, and federated newsletter subscribers. Trends show the last 14 days.
          </p>
        </div>
        <button type="button" onClick={load} className="text-zinc-500 hover:text-black text-[12px]" data-testid="cross-app-refresh">
          <RefreshCw size={12} className="inline mr-1" /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricCard
          icon={Activity}
          label="ERIO add-on calls"
          value={ac.total}
          sub={`+${ac.last_7_days} in 7d`}
          series={ac.series}
          color="#7737F6"
          testid="cross-app-card-calls"
        />
        <MetricCard
          icon={UserPlus}
          label="Leads from ERIO"
          value={lr.total}
          sub={`+${lr.last_7_days} in 7d`}
          series={lr.series}
          color="#1E9E5A"
          testid="cross-app-card-leads"
        />
        <MetricCard
          icon={Mail}
          label="Federated subscribers"
          value={data.federated_subscribers.total}
          sub="from ERIO"
          color="#E0A52A"
          testid="cross-app-card-subscribers"
        />
      </div>

      <div className="mt-5 bg-white border border-zinc-200 p-5">
        <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-zinc-700 mb-3 flex items-center gap-2">
          <ArrowUpRight size={13} className="text-[#7737F6]" /> Add-on demand by type
        </div>
        <div className="flex gap-8 text-[14px]">
          <div data-testid="cross-app-type-risk"><span className="text-zinc-500">Risk check</span> · <strong className="tabular-nums">{ac.by_type.risk}</strong></div>
          <div data-testid="cross-app-type-plan"><span className="text-zinc-500">Improvement plan</span> · <strong className="tabular-nums">{ac.by_type.plan}</strong></div>
        </div>
      </div>
    </div>
  );
};

export default CrossAppPanel;
